import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { SocialAccountStatus, UserResponse, UserSocialId } from '@business/entities/account';
import { CannedResponseType } from '@business/entities/canned';
import { GmailMessageResponse } from '@business/entities/gmail_messages';
import { CompactOrgResponse, CompactOrgSmallResponse, OrgId, OrgResponse } from '@business/entities/organization';
import { CommonService, FileService } from '@business/services';
import { PageContextService } from '@business/services/page_context_service';
import { Button } from '@core/components/Button';
import { FormAjaxSelect, FormInput, FormSelect } from '@core/components/form';
import { FormHTML } from '@core/components/form/FormHTML';
import { TopLoadingBar } from '@core/components/LoadingBar';
import { usePageInfoContext } from '@core/components/PageContext';
import { useBloc } from '@core/utils/bloc';
import { formControl, FormControl, FormDataType, formGroup, useFormConfig, useFormState, Validators } from '@core/utils/form';
import { isEmail } from '@core/utils/form/validation/validators/isEmail';
import { gcLabel } from '@core/utils/hooks/misc/gcLabel';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { R } from '@core/utils/r';
import { Session } from '@modules/auth/session';
import { openCannedResponseModal } from '@modules/common/SelectCannedResponse';
import { openMediaLinkModal } from '@modules/common/SelectMediaLinkResponse';
import { Col, InputGroup, Row } from 'reactstrap';
import { of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { NewEmailBloc } from './NewEmailBloc';
import { DropZoneFiles, DropZoneUploadedFile } from '@modules/common/DropZoneFiles';

function defineEmailForm() {
  return formGroup({
    orgId: formControl<OrgId>({
      placeholder: `Select ${gcLabel('Organization')}`,
    }),
    to: formControl<string[]>({
      placeholder: 'example@example.com',
      required: true,
      validators: [Validators.emails],
    }),
    cc: formControl<string[]>({
      placeholder: 'CC',
      validators: [Validators.emails],
    }),
    bcc: formControl<string[]>({
      placeholder: 'BCC',
      validators: [Validators.emails],
    }),
    subject: formControl<string>({
      placeholder: 'Subject',
      required: true,
    }),
    body: formControl<string>({
      placeholder: '',
      required: true,
    }),
    files: formControl<DropZoneUploadedFile[]>({
      label: 'Attachments',
    }),
    socialId: formControl<UserSocialId>({
      label: 'From',
      required: true,
    }),
    pageType: formControl<string>({}),
    pageId: formControl<string>({}),
  });
}

export type NewEmailFormData = FormDataType<typeof defineEmailForm>;

function signature(user: UserResponse, socialId: UserSocialId) {
  const social = R.find(user.socialAccounts, s => s.id === socialId);
  if (social && social.signature) {
    return `<br /><br /><div class="signature"><hr />${social.signature}</div>`;
  } else {
    return `<br /><br /><div class="gc-signature"><hr />${user.signature || ''}</div>`;
  }
  return '';
}

export interface NewEmailProps {
  emails: {
    email: string;
    name: string;
  }[];
  email?: GmailMessageResponse;
  isOrg?: boolean;
  submitCallback?(): void;
  toValue?: string;
  org?: OrgResponse | CompactOrgResponse | CompactOrgSmallResponse;
  emailShortcodes?: any;
}

const MAX_FILE_SIZE = 25; // 25MB
const MAX_TOTAL_SIZE = 25; // 25MB total

export const NewEmail = memo((props: NewEmailProps) => {
  const { emails, email, isOrg = false, submitCallback, toValue, org, emailShortcodes } = props;
  const user = useObservable(Session.user$);
  const bloc = useBloc(NewEmailBloc, user?.id || '0');
  const contacts = useObservable(bloc.contacts$, []);
  const otherEmails = useObservable(bloc.emails$, []);
  const form = useMemo(() => defineEmailForm(), []);
  const formValue = useNonNilObservable(form.value$);
  const [values, setValues] = useState<string[]>([]);
  const [showCC, setShowCC] = useState<boolean>(false);
  const [showDropZone, setShowDropZone] = useState<boolean>(true);
  const [hasFileErrors, setHasFileErrors] = useState(false);
  const pageContext = usePageInfoContext();

  const pageType = useMemo(() => (pageContext.current ? PageContextService.getTypeName(pageContext.current?.type) : ''), [pageContext]);
  const pageId = useMemo(() => pageContext.current?.object.id, [pageContext]);

  const orgItem = useMemo(() => org && { value: org.id, label: org.name, title: org.name }, [org]);

  const contactEmails = useMemo(
    () =>
      R.flatMap(contacts, contact =>
        R.map(contact.emails, email => {
          return {
            email: email.value,
            name: contact.name,
          };
        }),
      ),
    [contacts],
  );

  const allEmails = useMemo(() => {
    return R.uniqBy(
      R.concat(
        emails,
        R.map(values, value => {
          return {
            email: value,
            name: value,
          };
        }),
        otherEmails,
      ),
      'email',
    );
  }, [emails, otherEmails, values]);

  const cEmailItems = useMemo(
    () => CommonService.selectItems(contactEmails, s => (s.name !== s.email ? `${s.name} (${s.email})` : s.name), 'email'),
    [contactEmails],
  );

  const allEmailItems = useMemo(
    () =>
      CommonService.selectItems(
        R.differenceBy(allEmails, contactEmails, 'email'),
        s => {
          if (s.email === s.name || R.isEmpty(s.name)) return s.email;
          return `${s.name} (${s.email})`;
        },
        'email',
        true,
      ),
    [allEmails, contactEmails],
  );

  const valuesItems = useMemo(
    () =>
      CommonService.selectGroupedItems(
        [
          {
            label: 'Contacts',
            items: cEmailItems,
          },
          {
            label: 'Others',
            items: allEmailItems,
          },
        ],
        'label',
        s => s.items,
        'label',
        'value',
      ),
    [allEmailItems, cEmailItems],
  );

  const socialItems = useMemo(
    () =>
      CommonService.selectItems(
        user!.socialAccounts.filter(s => s.status === SocialAccountStatus.active),
        u => u.email + `( ${u.provider} )`,
        'id',
      ),
    [user],
  );

  // Check for file errors
  useEffect(() => {
    const hasErrors = (formValue.files || []).some(file => file.hasError);
    setHasFileErrors(hasErrors);
  }, [formValue.files]);

  useEffect(() => {
    if (socialItems.length === 1) {
      form.patchValue({
        socialId: socialItems[0].value,
      });
    }
  }, [form, socialItems]);

  useEffect(() => {
    if (user) {
      form.patchValue({
        body: (!!formValue.body ? formValue.body : '') + signature(user, formValue.socialId),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, user, formValue.socialId]);

  useEffect(() => {
    if (email) {
      form.patchValue({
        to: R.map(email.to, e => e.email),
        cc: R.map(email.cc, e => e.email),
        bcc: R.map(email.bcc, e => e.email),
        subject: email.subject,
      });
    }
  }, [email, form]);

  useEffect(() => {
    if (toValue) {
      form.patchValue({
        to: [toValue],
        orgId: org?.id,
      });
      org && bloc.fetchContacts(org.id);
    }
  }, [toValue, form, org, bloc]);

  useEffect(() => {
    if (formValue.orgId) {
      bloc.fetchContacts(formValue.orgId);
    }
  }, [formValue.orgId, bloc]);

  useEffect(() => {
    form.patchValue({
      pageId,
      pageType,
    });
  }, [pageId, pageType, form]);

  const onValueAdd = useCallback(
    (value, control: FormControl) => {
      if (isEmail(value)) {
        setValues([...values, value]);
        control.setValue([...(control.value || []), value]);
      }
    },
    [values],
  );

  const onSubmit = useCallback(
    (data: NewEmailFormData) => {
      // Prevent submission if there are file errors
      if (hasFileErrors) {
        return of(null);
      }

      return of(null).pipe(
        switchMap(() => {
          // Filter out files with errors
          const validFiles = (data.files || []).filter(f => !f.hasError);
          const submissionData = { ...data, files: validFiles };

          if (email) {
            return bloc.sendReply(email.id, submissionData);
          } else {
            return bloc.sendEmail(submissionData);
          }
        }),
        tap(() =>
          form.patchValue(
            {
              subject: email ? email.subject : '',
              socialId: socialItems.length === 1 ? socialItems[0].value : '',
              body: signature(user!, socialItems.length === 1 ? socialItems[0].value : ''),
              files: [],
            },
            { emit: false },
          ),
        ),
        tap(() => submitCallback && submitCallback()),
      );
    },
    [bloc, email, form, socialItems, submitCallback, user, hasFileErrors],
  );

  useFormConfig(form, { onSubmit });

  const { submitting } = useFormState(form, { submitting: true });

  if (!user) return <TopLoadingBar />;

  return (
    <>
      <div className="d-flex" style={{ flexDirection: 'column' }}>
        {!!isOrg && <FormAjaxSelect control={form.controls.orgId} initalValue={orgItem} callback={search => bloc.searchOrganizations(search)} />}
        <div className="form-group">
          <InputGroup className="d-flex align-items-center justify-content-center">
            <span style={{ flex: 1 }}>
              <FormSelect
                type="multiple"
                control={form.controls.to}
                items={valuesItems}
                withoutGroup
                onAdd={value => onValueAdd(value, form.controls.to)}
              />
            </span>
            <span className="ml-2" style={{ textAlign: 'right' }}>
              <a className="text-primary" onClick={() => setShowCC(!showCC)}>
                CC/BCC &nbsp;&nbsp;
                <i className={`fa ${showCC ? 'fa-caret-up' : 'fa-caret-down'}`}></i>
              </a>
            </span>
          </InputGroup>
        </div>
        <Row>
          <Col>
            <div className="form-group" style={{ display: showCC ? 'block' : 'none' }}>
              <FormSelect
                type="multiple"
                control={form.controls.cc}
                items={valuesItems}
                withoutGroup
                onAdd={value => onValueAdd(value, form.controls.cc)}
              />
            </div>
          </Col>
          <Col>
            <div className="form-group" style={{ display: showCC ? 'block' : 'none' }}>
              <FormSelect
                type="multiple"
                control={form.controls.bcc}
                items={valuesItems}
                withoutGroup
                onAdd={value => onValueAdd(value, form.controls.bcc)}
              />
            </div>
          </Col>
        </Row>
        {!email?.subject && <FormInput control={form.controls.subject} />}
        <FormHTML
          control={form.controls.body}
          full
          minHeight={500}
          variables={emailShortcodes}
          spellcheck
          canned={async initialText => {
            const response$ = await openCannedResponseModal({
              initialText,
              type: CannedResponseType.email,
              title: 'Email Templates',
              singularTitle: 'Template',
              pluralTitle: 'Templates',
              shortCodes: emailShortcodes,
            });
            return response$.pipe(
              tap(response => {
                if (response.subject && !formValue.subject) {
                  form.patchValue({ subject: response.subject });
                }
              }),
              switchMap(response => of(response.content)),
            );
          }}
          cannedName={'Templates'}
          media={initialText => openMediaLinkModal({ initialText })}
        />
        {socialItems.length > 1 && <FormSelect items={socialItems} control={form.controls.socialId} />}

        <a className="text-primary mb-2" onClick={() => setShowDropZone(!showDropZone)}>
          <i className={`bx ${showDropZone ? 'bx-chevron-up' : 'bx-chevron-down'} mr-1`}></i>
          Attach Files
        </a>

        {showDropZone && (
          <div className="mb-3">
            <DropZoneFiles
              small
              files={formValue.files || []}
              setUploadedFiles={(files: DropZoneUploadedFile[]) => form.patchValue({ files })}
              removeFile={index =>
                form.patchValue({
                  files: R.filter(formValue.files || [], (f, i) => i !== index),
                })
              }
              label="Drop files here or click to select"
              maxFileSize={FileService.bytesFromMegaBytes(MAX_FILE_SIZE)}
              maxTotalSize={FileService.bytesFromMegaBytes(MAX_TOTAL_SIZE)}
            />
          </div>
        )}

        <div className="form-group mt-3">
          <Button intent="primary" style={{ float: 'right', width: 150 }} loading={submitting} disabled={hasFileErrors} onClick={() => form.submit()}>
            {`Send`}
          </Button>
        </div>
      </div>
    </>
  );
});
